/**
 * SEO helpers — DOM-level meta / link / JSON-LD management for the SPA.
 * Re-applied on every route change. All setters are find-or-create
 * (no duplicates).
 */
import { useEffect } from "react";

const SITE_NAME = "Lecis Auto Glass";
const DEFAULT_OG_IMAGE = "https://images.pexels.com/photos/1719648/pexels-photo-1719648.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630&fit=crop";

function origin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

function absUrl(pathOrUrl) {
  if (!pathOrUrl) return origin();
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : "/" + pathOrUrl;
  return origin() + path;
}

function upsertMetaByName(name, content) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content ?? "");
}

function upsertMetaByProperty(property, content) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content ?? "");
}

export function setTitle(title) {
  if (typeof document !== "undefined" && title) document.title = title;
}

export function setMetaDescription(desc) {
  upsertMetaByName("description", desc || "");
}

export function setRobots(value) {
  upsertMetaByName("robots", value || "index,follow");
}

export function setCanonical(pathOrUrl) {
  if (typeof document === "undefined") return;
  const href = absUrl(pathOrUrl);
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Set (or remove when falsy) a <link rel="prev|next"> for paginated pages. */
export function setLinkRel(rel, pathOrUrl) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!pathOrUrl) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", absUrl(pathOrUrl));
}

export function setOpenGraph({ title, description, url, image, type = "website" }) {
  upsertMetaByProperty("og:site_name", SITE_NAME);
  upsertMetaByProperty("og:type", type);
  upsertMetaByProperty("og:title", title || "");
  upsertMetaByProperty("og:description", description || "");
  upsertMetaByProperty("og:url", absUrl(url));
  upsertMetaByProperty("og:image", absUrl(image || DEFAULT_OG_IMAGE));
  upsertMetaByProperty("og:image:width", "1200");
  upsertMetaByProperty("og:image:height", "630");
  upsertMetaByName("twitter:card", "summary_large_image");
  upsertMetaByName("twitter:title", title || "");
  upsertMetaByName("twitter:description", description || "");
  upsertMetaByName("twitter:image", absUrl(image || DEFAULT_OG_IMAGE));
}

/** Inject a JSON-LD <script>. id is a logical key — same id replaces. */
export function setJsonLd(id, data) {
  if (typeof document === "undefined") return;
  if (!data) return removeJsonLd(id);
  const domId = `ld-${id}`;
  let el = document.getElementById(domId);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = domId;
    document.head.appendChild(el);
  }
  try {
    el.textContent = JSON.stringify(data);
  } catch {
    el.textContent = "{}";
  }
}

export function removeJsonLd(id) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(`ld-${id}`);
  if (el) el.remove();
}

export function clearJsonLdGroup(ids) {
  ids.forEach(removeJsonLd);
}

/**
 * React hook — declarative SEO per page.
 *   useSeo({
 *     title, description, canonicalPath, robots,
 *     og: { image, type },
 *     jsonLd: { product: {...}, breadcrumb: {...} },
 *   })
 * On unmount: removes any JSON-LD ids that were declared.
 */
export function useSeo({ title, description, canonicalPath, robots, og, jsonLd, prevPath, nextPath } = {}) {
  useEffect(() => {
    if (title) setTitle(title);
    setMetaDescription(description || "");
    if (canonicalPath) setCanonical(canonicalPath);
    setRobots(robots || "index,follow");
    setLinkRel("prev", prevPath || null);
    setLinkRel("next", nextPath || null);
    setOpenGraph({
      title: title || "",
      description: description || "",
      url: canonicalPath || (typeof window !== "undefined" ? window.location.pathname : "/"),
      image: og?.image,
      type: og?.type,
    });
    const ids = Object.keys(jsonLd || {});
    ids.forEach((id) => setJsonLd(id, jsonLd[id]));
    return () => {
      ids.forEach((id) => removeJsonLd(id));
      setLinkRel("prev", null);
      setLinkRel("next", null);
    };
    // re-run when any string in the dependency tuple changes
    // eslint-disable-next-line
  }, [title, description, canonicalPath, robots, prevPath, nextPath, JSON.stringify(og || {}), JSON.stringify(jsonLd || {})]);
}

export const SITE = { name: SITE_NAME };
